import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useWeb3 } from '../contexts/Web3Context';
import api, { API_ENDPOINTS } from '../config/api';
import { depositEscrow, inProgressEscrow, completeEscrow, disburseEscrow, confirmEscrow, getEthPriceUSD, getJobDetails, verifyDepositOnMainnet, findDepositTxForChatId } from '../config/Escrowintegration';
import { Contract, formatUnits, parseUnits } from 'ethers';
import { USDC_TOKEN_ABI, USDC_TOKEN_ADDRESS } from '../config/Constants';
import axios from 'axios';
import { 
  ArrowLeft, 
  Send, 
  Paperclip, 
  Image, 
  Smile, 
  MoreVertical,
  Phone,
  Video,
  Info,
  DollarSign,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  Download,
  Eye,
  EyeOff,
  Link,
  ExternalLink,
  File,
  Archive,
  Search,
  Edit3,
  ChevronRight,
  ExternalLink as ExternalLinkIcon,
  TrendingUp,
  TrendingDown,
  Settings,
  X
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

const ChatDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { socket } = useSocket();
  const {
    isConnected: isWalletConnected,
    walletAddress,
    connectWallet,
    shortenAddress,
    signer,
    provider,
    walletBalance,
    ethBalance
  } = useWeb3();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [showInfo, setShowInfo] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [newPrice, setNewPrice] = useState('');
  const [quickAccessFilter, setQuickAccessFilter] = useState('all'); // 'all', 'links', 'images', 'files'
  const [showPriceResetModal, setShowPriceResetModal] = useState(false);
  const [resetPrice, setResetPrice] = useState('');
  const [resetReason, setResetReason] = useState('');
  // Default to 'chat' tab if user is an added participant
  const [activeTab, setActiveTab] = useState('progress');
  const [escrowLoading, setEscrowLoading] = useState({
    deposit: false,
    inProgress: false,
    complete: false,
    disburse: false,
    confirm: false
  });
  const [showDisburseModal, setShowDisburseModal] = useState(false);
  const [disburseAmount, setDisburseAmount] = useState('');
  const [jobDetails, setJobDetails] = useState(null);
  const [loadingJobDetails, setLoadingJobDetails] = useState(false);
  const [ethPrice, setEthPrice] = useState(null);
  const [participantSearch, setParticipantSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [showRemoveParticipantModal, setShowRemoveParticipantModal] = useState(false);
  const [participantToRemove, setParticipantToRemove] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [messageContent, setMessageContent] = useState('');

  // Fetch chat details and messages
  const { data: chatData, isLoading } = useQuery(
    ['chat', id],
    () => api.get(API_ENDPOINTS.CHATS.DETAIL(id)),
    {
      select: (data) => data.data,
      enabled: !!id
    }
  );

  // Check if chat has an active dispute proposal
  const { data: proposalData } = useQuery(
    ['chat-proposal', id],
    () => api.get(API_ENDPOINTS.GOVERNANCE.CHAT_PROPOSAL(id)),
    {
      select: (data) => data.data,
      enabled: !!id,
      retry: false,
      refetchOnWindowFocus: false
    }
  );

  const hasActiveProposal = proposalData?.hasActiveProposal || false;

  // If there's an active proposal, default to chat tab and prevent switching to progress
  useEffect(() => {
    if (hasActiveProposal && activeTab === 'progress') {
      setActiveTab('chat');
    }
  }, [hasActiveProposal, activeTab]);

  const chat = chatData?.chat;
  const messages = chatData?.messages || [];

  // Fetch USDC price for USD conversion (treated as ~1:1)
  const { data: ethPriceData } = useQuery(
    'usdcPrice',
    async () => {
      try {
        // Use the same endpoint as Escrowintegration.js (which works for transactions)
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await axios.get(`${API_URL}/api/v1/price/usdc`);
        return response.data;
      } catch (error) {
        console.error("Failed to get USDC price from backend:", error);
        return null;
      }
    },
    {
      select: (data) => data,
      staleTime: 300000, // Cache for 5 minutes
      refetchInterval: 300000, // Refetch every 5 minutes
      onSuccess: (data) => {
        if (data?.usd) {
          setEthPrice(data.usd);
        }
      }
    }
  );

  // Update ethPrice state when data changes
  useEffect(() => {
    if (ethPriceData?.usd) {
      setEthPrice(ethPriceData.usd);
    }
  }, [ethPriceData]);

  const getParticipantByRole = (role) =>
    chat?.participants?.find((participant) => participant.role === role);

  const clientParticipant = getParticipantByRole('client');
  const talentParticipant = getParticipantByRole('talent');
  
  // Check if current user is an added participant (not owner)
  const currentUserParticipant = chat?.participants?.find(
    p => p.user._id === user?._id || p.user === user?._id
  );
  const isAddedParticipant = currentUserParticipant?.participantType === 'added';
  const isOwner = currentUserParticipant?.participantType === 'owner';

  // Set default tab based on participant type - must be after chat and currentUserParticipant are defined
  useEffect(() => {
    if (chat && currentUserParticipant) {
      if (currentUserParticipant.participantType === 'added') {
        setActiveTab('chat');
      }
    }
  }, [chat, currentUserParticipant]);

  const normalizeAddress = (address) =>
    address ? address.toLowerCase() : address;

  const displayAddress = (address) => {
    if (!address) return '';
    if (shortenAddress) return shortenAddress(address);
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const updateEscrowLoading = (key, value) =>
    setEscrowLoading((prev) => ({ ...prev, [key]: value }));

  const invalidateChatData = () => {
    queryClient.invalidateQueries(['chat', id]);
    queryClient.invalidateQueries(['chats']);
    queryClient.invalidateQueries('walletTransactions');
    queryClient.invalidateQueries('dashboardStats');
    if (user?._id) {
      queryClient.invalidateQueries(['userStats', user._id]);
    }
  };

  // Send message mutation
  // Removed sendMessageMutation - now using direct API call in handleSendMessage

  // Update chat status mutation
  const updateStatusMutation = useMutation(
    (status) => api.put(API_ENDPOINTS.CHATS.UPDATE_STATUS(id), { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['chat', id]);
        toast.success('Status updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update status');
      }
    }
  );

  // Update workflow status mutation
  const updateWorkflowStatusMutation = useMutation(
    (workflowStatus) => api.put(API_ENDPOINTS.CHATS.UPDATE_WORKFLOW_STATUS(id), { workflowStatus }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['chat', id]);
        toast.success('Workflow status updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update workflow status');
      }
    }
  );

  // Update price mutation
  const updatePriceMutation = useMutation(
    (data) => api.put(API_ENDPOINTS.CHATS.UPDATE_PRICE(id), data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['chat', id]);
        setShowPriceResetModal(false);
        setResetPrice('');
        setResetReason('');
        toast.success('Price updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update price');
      }
    }
  );

  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch job details when in-progress
  useEffect(() => {
    if (chat?.workflowStatus === 'in-progress' && chat?.escrow?.inProgress?.txHash && isWalletConnected) {
      fetchJobDetailsFromBlockchain();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?.workflowStatus, chat?.escrow?.inProgress?.txHash, isWalletConnected]);

  // Socket event listeners
  useEffect(() => {
    if (socket && chat) {
      socket.emit('join-chat', chat._id);

      const handleNewMessage = (message) => {
        console.log('New message received:', message);
        if (message.chatId === chat._id) {
          // Invalidate queries to refetch messages
          queryClient.invalidateQueries(['chat', id]);
          
          // Also invalidate chat list to update unread counts
          queryClient.invalidateQueries(['chats']);
          queryClient.invalidateQueries(['unreadMessages']);
          
          // Scroll to bottom when new message arrives
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      };

      const handleWorkflowStatusUpdate = (data) => {
        if (data.chatId === chat._id) {
          queryClient.invalidateQueries(['chat', id]);
          toast.success(`Workflow status updated to ${data.workflowStatus.replace('-', ' ')}`);
        }
      };

      const handlePriceUpdate = (data) => {
        if (data.chatId === chat._id) {
          queryClient.invalidateQueries(['chat', id]);
          toast.success(`Price updated to $${data.newPrice}`);
        }
      };

      const handleEscrowUpdate = (data) => {
        if (data.chatId === chat._id) {
          queryClient.invalidateQueries(['chat', id]);
          queryClient.invalidateQueries('walletTransactions');
        }
      };

      socket.on('new-message', handleNewMessage);
      socket.on('workflow-status-updated', handleWorkflowStatusUpdate);
      socket.on('price-updated', handlePriceUpdate);
      socket.on('escrow-updated', handleEscrowUpdate);

      return () => {
        socket.emit('leave-chat', chat._id);
        socket.off('new-message', handleNewMessage);
        socket.off('workflow-status-updated', handleWorkflowStatusUpdate);
        socket.off('price-updated', handlePriceUpdate);
        socket.off('escrow-updated', handleEscrowUpdate);
      };
    }
  }, [socket, chat, id, queryClient]);

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    const content = messageContent.trim();
    const hasFiles = selectedFiles.length > 0;
    
    if (!content && !hasFiles) {
      return;
    }

    try {
      const formData = new FormData();
      if (content) {
        formData.append('content', content);
      }
      formData.append('type', hasFiles ? (selectedFiles.some(f => f.type.startsWith('image/')) ? 'image' : 'file') : 'text');
      
      // Append files
      selectedFiles.forEach((fileObj, index) => {
        formData.append('attachments', fileObj.file);
      });

      // Send message with attachments
      await api.post(API_ENDPOINTS.CHATS.SEND_MESSAGE(id), formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Clear form
      setMessageContent('');
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Invalidate queries to refresh messages
      queryClient.invalidateQueries(['chat', id]);
      toast.success('Message sent');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.response?.data?.error || 'Failed to send message');
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    // Validate file sizes
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast.error(`File ${file.name} exceeds 10MB limit`);
        return false;
      }
      return true;
    });

    // Add previews for images
    const filesWithPreview = validFiles.map(file => {
      const fileObj = {
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      };
      return fileObj;
    });

    setSelectedFiles(prev => [...prev, ...filesWithPreview]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => {
      const newFiles = [...prev];
      // Revoke object URL if it's an image preview
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const handleStatusUpdate = (status) => {
    updateStatusMutation.mutate(status);
  };

  // Search users for adding to chat
  const searchUsers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.get(API_ENDPOINTS.CHATS.SEARCH_USERS, {
        params: { q: query }
      });
      // Filter out users already in chat
      const existingUserIds = chat?.participants?.map(p => 
        p.user._id || p.user
      ) || [];
      const filtered = response.data.users.filter(
        u => !existingUserIds.includes(u._id)
      );
      setSearchResults(filtered);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (participantSearch) {
        searchUsers(participantSearch);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [participantSearch]);

  // Add participant mutation
  const addParticipantMutation = useMutation(
    (userId) => api.post(API_ENDPOINTS.CHATS.ADD_PARTICIPANT(id), { userId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['chat', id]);
        setParticipantSearch('');
        setSearchResults([]);
        setShowAddParticipant(false);
        toast.success('Participant added successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to add participant');
      }
    }
  );

  // Remove participant mutation
  const removeParticipantMutation = useMutation(
    (userId) => api.delete(API_ENDPOINTS.CHATS.REMOVE_PARTICIPANT(id, userId)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['chat', id]);
        toast.success('Participant removed successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to remove participant');
      }
    }
  );

  const handleAddParticipant = (userId) => {
    addParticipantMutation.mutate(userId);
  };

  const handleRemoveParticipant = (userId, participant) => {
    setParticipantToRemove({ userId, participant });
    setShowRemoveParticipantModal(true);
  };

  const confirmRemoveParticipant = () => {
    if (participantToRemove) {
      removeParticipantMutation.mutate(participantToRemove.userId);
      setShowRemoveParticipantModal(false);
      setParticipantToRemove(null);
    }
  };

  const ensureWalletConnection = async () => {
    if (isWalletConnected && walletAddress) {
      return true;
    }

    const result = await connectWallet();
    if (result && result.address) {
      return true;
    }

    toast.error('Please connect your wallet to continue.');
    return false;
  };

  const resolveSigner = async () => {
    if (signer) return signer;
    if (provider) {
      try {
        return await provider.getSigner();
      } catch (error) {
        console.error('Unable to resolve signer from provider', error);
      }
    }
    return null;
  };

  const handleWorkflowStatusUpdate = (workflowStatus) => {
    updateWorkflowStatusMutation.mutate(workflowStatus);
  };

  const handleWorkflowStepClick = async (stepId) => {
    switch (stepId) {
      case 'deposit':
        await handleEscrowDeposit();
        return;
      case 'in-progress':
        await handleEscrowInProgress();
        return;
      case 'completed':
        await handleEscrowComplete();
        return;
      case 'confirmed':
        await handleEscrowConfirm();
        return;
      default:
        handleWorkflowStatusUpdate(stepId);
    }
  };

  const handlePriceReset = () => {
    if (!resetPrice || isNaN(resetPrice)) {
      toast.error('Please enter a valid price');
      return;
    }
    updatePriceMutation.mutate({
      price: parseFloat(resetPrice),
      reason: resetReason
    });
  };

  const determineUsdAmount = () => {
    if (!chat) return 0;
    if (chat.price?.current) return Number(chat.price.current);
    if (chat.job?.budget?.fixed) return Number(chat.job.budget.fixed);
    if (chat.gig?.pricing?.basic) return Number(chat.gig.pricing.basic);
    return 0;
  };

  const resolveTalentWallet = () => {
    const escrowWallet = chat?.escrow?.deposit?.toAddress;
    if (escrowWallet) {
      return escrowWallet;
    }
    const wallet = talentParticipant?.user?.walletAddress;
    return wallet ? wallet : null;
  };

  const resolveJobOrGigId = () => {
    if (chat?.job) {
      const j = chat.job;
      return String(j._id || j.id || j);
    }
    if (chat?.gig) {
      const g = chat.gig;
      return String(g._id || g.id || g);
    }
    return '';
  };

  const buildEscrowIdentifiers = () => {
    // On-chain orders are keyed by chat Mongo id — never use gig/job id as chatId
    const chatId = String(chat?._id || id || '');

    if (chat?.escrow?.identifiers) {
      console.log('Using stored identifiers from deposit:', chat.escrow.identifiers);
      return {
        jobId: chat.escrow.identifiers.jobId || resolveJobOrGigId(),
        customerId: chat.escrow.identifiers.customerId || '',
        talentId: chat.escrow.identifiers.talentId || '',
        chatId: chat.escrow.identifiers.chatId || chatId
      };
    }

    return {
      jobId: resolveJobOrGigId(),
      customerId: clientParticipant?.user?._id ? String(clientParticipant.user._id) : '',
      talentId: talentParticipant?.user?._id ? String(talentParticipant.user._id) : '',
      chatId
    };
  };

  const handleEscrowDeposit = async () => {
    if (!chat) return;

    if (!clientParticipant || clientParticipant.user._id !== user?._id) {
      toast.error('Only the client can initiate a deposit.');
      return;
    }

    const usdAmount = determineUsdAmount();
    if (!usdAmount || Number.isNaN(usdAmount) || usdAmount <= 0) {
      toast.error('Unable to determine project amount for deposit.');
      return;
    }

    const connected = await ensureWalletConnection();
    if (!connected) return;

    // Check if user has sufficient balance for deposit
    try {
      const ethPrice = await getEthPriceUSD();
      const requiredUsdcAmount = Number(usdAmount) / Number(ethPrice);
      const currentUsdcBalance = parseFloat(walletBalance || '0');
      
      if (currentUsdcBalance < requiredUsdcAmount) {
        toast.error('Insufficient balance. Please ensure your wallet has enough USDC to cover the deposit amount.');
        return;
      }
    } catch (error) {
      console.error('Error checking balance:', error);
      toast.error('Unable to verify wallet balance. Please try again.');
      return;
    }

    const signerInstance = await resolveSigner();
    if (!signerInstance) {
      toast.error('Unable to access wallet signer.');
      return;
    }

    updateEscrowLoading('deposit', true);
    let identifiers;
    try {
      identifiers = buildEscrowIdentifiers();
      if (!identifiers.jobId || !identifiers.customerId || !identifiers.talentId || !identifiers.chatId) {
        toast.error('Missing project identifiers. Please refresh the page and try again.');
        return;
      }

      const recordEscrowDeposit = async ({ txHash, usdcAmount }) => {
        await verifyDepositOnMainnet({
          txHash,
          chatId: identifiers.chatId
        });
        await api.post(API_ENDPOINTS.CHATS.ESCROW_DEPOSIT(id), {
          txHash,
          fromAddress: walletAddress,
          amountUSD: usdAmount,
          amountETH: usdcAmount,
          customerWallet: walletAddress,
          jobId: identifiers.jobId,
          customerId: identifiers.customerId,
          talentId: identifiers.talentId,
          chatId: identifiers.chatId
        });
        sessionStorage.removeItem(`escrow-deposit-tx-${id}`);
      };

      // Recovery: on-chain deposit exists but backend was never updated (relayer verify race).
      if (!chat.escrow?.deposit?.txHash && chat.workflowStatus === 'offered') {
        const existingOrder = await getJobDetails({
          signer: signerInstance,
          chatId: identifiers.chatId
        });
        if (existingOrder?.status === '1') {
          const recoveryTx =
            sessionStorage.getItem(`escrow-deposit-tx-${id}`) ||
            (await findDepositTxForChatId({ chatId: identifiers.chatId }));
          if (recoveryTx) {
            const ethPrice = await getEthPriceUSD();
            const usdcAmount = Number(usdAmount) / Number(ethPrice);
            await recordEscrowDeposit({ txHash: recoveryTx, usdcAmount });
            toast.success('Escrow deposit recovered and recorded.');
            invalidateChatData();
            return;
          }
          toast.error(
            'A deposit already exists on-chain for this chat but could not be synced automatically. Please contact support.'
          );
          return;
        }
      }

      // Log current job status
      console.log('=== Escrow Deposit Attempt ===');
      console.log('Chat ID:', id);
      console.log('Current workflow status:', chat.workflowStatus);
      console.log('Job ID:', identifiers.jobId);
      console.log('Customer ID:', identifiers.customerId);
      console.log('Talent ID:', identifiers.talentId);
      console.log('USD Amount:', usdAmount);
      console.log('================================');

      const { relayed, txHash, usdcAmount } = await depositEscrow({
        signer: signerInstance,
        jobId: identifiers.jobId,
        customerId: identifiers.customerId,
        talentId: identifiers.talentId,
        customerWallet: walletAddress,
        chatId: identifiers.chatId,
        usdAmount,
        ethBalance
      });

      if (!relayed || !txHash) {
        throw new Error('Relayer did not return a transaction hash');
      }
      console.log('Relayed escrow deposit submitted:', txHash);

      await recordEscrowDeposit({ txHash, usdcAmount });

      console.log('Backend updated successfully');
      toast.success(
        `Escrow deposit submitted from ${displayAddress(walletAddress)}.`
      );
      invalidateChatData();
    } catch (error) {
      console.error('Deposit error:', error);
      const recoveryTx = error?.response?.data?.txHash;
      if (recoveryTx && identifiers?.chatId) {
        sessionStorage.setItem(`escrow-deposit-tx-${id}`, recoveryTx);
        try {
          const ethPrice = await getEthPriceUSD();
          const usdcAmount = Number(usdAmount) / Number(ethPrice);
          await verifyDepositOnMainnet({
            txHash: recoveryTx,
            chatId: identifiers.chatId
          });
          await api.post(API_ENDPOINTS.CHATS.ESCROW_DEPOSIT(id), {
            txHash: recoveryTx,
            fromAddress: walletAddress,
            amountUSD: usdAmount,
            amountETH: usdcAmount,
            customerWallet: walletAddress,
            jobId: identifiers.jobId,
            customerId: identifiers.customerId,
            talentId: identifiers.talentId,
            chatId: identifiers.chatId
          });
          sessionStorage.removeItem(`escrow-deposit-tx-${id}`);
          toast.success('Escrow deposit recovered and recorded.');
          invalidateChatData();
          return;
        } catch (recoveryError) {
          console.warn('Deposit recovery failed:', recoveryError);
        }
      }
      const serverMessage = error?.response?.data?.error || error?.message || 'Deposit failed';
      const details = error?.response?.data?.details || error?.response?.data?.message;
      toast.error(details ? `${serverMessage}: ${details}` : serverMessage);
    } finally {
      updateEscrowLoading('deposit', false);
    }
  };

  const fetchJobDetailsFromBlockchain = async () => {
    if (!chat) return;
    
    const identifiers = buildEscrowIdentifiers();
    if (!identifiers.chatId) return;

    const signerInstance = await resolveSigner();
    if (!signerInstance) return;

    setLoadingJobDetails(true);
    try {
      const details = await getJobDetails({
        signer: signerInstance,
        chatId: identifiers.chatId,
        jobId: identifiers.jobId // Keep for backward compatibility
      });

      if (!details) {
        setJobDetails(null);
        console.warn(
          'No on-chain order for chatId',
          identifiers.chatId,
          '— deposit may have failed or used a different contract/chatId'
        );
        return;
      }

      let usdcDecimals = 6;
      try {
        const token = new Contract(USDC_TOKEN_ADDRESS, USDC_TOKEN_ABI, signerInstance.provider || signerInstance);
        usdcDecimals = Number(await token.decimals());
      } catch (e) {
        // keep default
      }

      setJobDetails({
        ...details,
        // New fields (USDC)
        totalAmountUSDC: Number(formatUnits(details.totalAmount, usdcDecimals)),
        remainingAmountUSDC: Number(formatUnits(details.remainingAmount, usdcDecimals)),
        disbursedAmountsUSDC: details.disbursedAmounts.map(amt => Number(formatUnits(amt, usdcDecimals))),
        // Backward-compatible fields still used throughout the UI (now carry USDC)
        totalAmountETH: Number(formatUnits(details.totalAmount, usdcDecimals)),
        remainingAmountETH: Number(formatUnits(details.remainingAmount, usdcDecimals)),
        disbursedAmountsETH: details.disbursedAmounts.map(amt => Number(formatUnits(amt, usdcDecimals)))
      });
    } catch (error) {
      console.error('Error fetching job details:', error);
      if (!String(error?.message || '').includes('Order does not exist')) {
        toast.error('Failed to fetch job details from blockchain');
      }
    } finally {
      setLoadingJobDetails(false);
    }
  };

  const handleEscrowInProgress = async () => {
    if (!chat) return;

    if (!talentParticipant || talentParticipant.user._id !== user?._id) {
      toast.error('Only the talent can mark the project as in-progress.');
      return;
    }

    const connected = await ensureWalletConnection();
    if (!connected) return;

    const signerInstance = await resolveSigner();
    if (!signerInstance) {
      toast.error('Unable to access wallet signer.');
      return;
    }

    updateEscrowLoading('inProgress', true);
    try {
      const identifiers = buildEscrowIdentifiers();
      if (!identifiers.jobId || !identifiers.customerId || !identifiers.talentId || !identifiers.chatId) {
        toast.error('Missing project identifiers. Please refresh the page and try again.');
        return;
      }

      console.log('=== Escrow In-Progress Attempt ===');
      console.log('Chat ID:', id);
      console.log('Current workflow status:', chat.workflowStatus);
      console.log('Job ID:', identifiers.jobId);
      console.log('Talent Wallet:', walletAddress);
      console.log('==================================');

      const { relayed, txHash } = await inProgressEscrow({
        signer: signerInstance,
        jobId: identifiers.jobId,
        customerId: identifiers.customerId,
        talentId: identifiers.talentId,
        talentWallet: walletAddress,
        chatId: identifiers.chatId
      });
      if (!relayed || !txHash) {
        throw new Error('Relayer did not return a transaction hash');
      }
      console.log('Relayed in-progress submitted:', txHash);

      await api.post(API_ENDPOINTS.CHATS.ESCROW_IN_PROGRESS(id), {
        txHash,
        fromAddress: walletAddress,
        jobId: identifiers.jobId,
        customerId: identifiers.customerId,
        talentId: identifiers.talentId,
        chatId: identifiers.chatId
      });

      console.log('Backend updated successfully');
      toast.success('Work in-progress status recorded successfully.');
      invalidateChatData();
      // Fetch updated job details
      await fetchJobDetailsFromBlockchain();
    } catch (error) {
      console.error('In-progress error:', error);
      const serverMessage = error?.response?.data?.error || error?.message || 'In-progress failed';
      const details = error?.response?.data?.details;
      toast.error(details ? `${serverMessage}: ${details}` : serverMessage);
    } finally {
      updateEscrowLoading('inProgress', false);
    }
  };

  const handleEscrowDisburse = async () => {
    if (!chat) return;

    if (!clientParticipant || clientParticipant.user._id !== user?._id) {
      toast.error('Only the client can disburse funds.');
      return;
    }

    if (!disburseAmount || isNaN(disburseAmount) || Number(disburseAmount) <= 0) {
      toast.error('Please enter a valid disbursement amount.');
      return;
    }

    const usdAmount = Number(disburseAmount);
    const connected = await ensureWalletConnection();
    if (!connected) return;

    const signerInstance = await resolveSigner();
    if (!signerInstance) {
      toast.error('Unable to access wallet signer.');
      return;
    }

    updateEscrowLoading('disburse', true);
    try {
      const identifiers = buildEscrowIdentifiers();
      if (!identifiers.jobId || !identifiers.customerId || !identifiers.talentId || !identifiers.chatId) {
        toast.error('Missing project identifiers. Please refresh the page and try again.');
        return;
      }

      // USDC is treated as ~1:1 with USD in backend pricing.
      const price = await getEthPriceUSD();
      const amountUSDC = usdAmount / Number(price);
      let usdcDecimals = 6;
      try {
        const token = new Contract(USDC_TOKEN_ADDRESS, USDC_TOKEN_ABI, signerInstance.provider || signerInstance);
        usdcDecimals = Number(await token.decimals());
      } catch (e) {
        // keep default
      }
      const parsedAmount = parseUnits(amountUSDC.toFixed(usdcDecimals), usdcDecimals);

      console.log('=== Escrow Disbursement Attempt ===');
      console.log('Chat ID:', id);
      console.log('USD Amount:', usdAmount);
      console.log('USDC Amount:', amountUSDC);
      console.log('Job ID:', identifiers.jobId);
      console.log('==================================');

      const { relayed, txHash } = await disburseEscrow({
        signer: signerInstance,
        jobId: identifiers.jobId,
        customerId: identifiers.customerId,
        talentId: identifiers.talentId,
        customerWallet: walletAddress,
        amount: parsedAmount,
        chatId: identifiers.chatId
      });
      if (!relayed || !txHash) {
        throw new Error('Relayer did not return a transaction hash');
      }
      console.log('Relayed disbursement submitted:', txHash);

      await api.post(API_ENDPOINTS.CHATS.ESCROW_DISBURSE(id), {
        txHash,
        fromAddress: walletAddress,
        amountUSD: usdAmount,
        // Backend still expects `amountETH` field; it now carries USDC amount.
        amountETH: amountUSDC,
        jobId: identifiers.jobId,
        customerId: identifiers.customerId,
        talentId: identifiers.talentId,
        chatId: identifiers.chatId
      });

      console.log('Backend updated successfully');
      toast.success(`Disbursement of $${usdAmount.toFixed(2)} recorded successfully.`);
      setShowDisburseModal(false);
      setDisburseAmount('');
      invalidateChatData();
      // Fetch updated job details
      await fetchJobDetailsFromBlockchain();
    } catch (error) {
      console.error('Disbursement error:', error);
      const serverMessage = error?.response?.data?.error || error?.message || 'Disbursement failed';
      const details = error?.response?.data?.details;
      toast.error(details ? `${serverMessage}: ${details}` : serverMessage);
    } finally {
      updateEscrowLoading('disburse', false);
    }
  };

  const handleEscrowComplete = async () => {
    if (!chat) return;

    if (!talentParticipant || talentParticipant.user._id !== user?._id) {
      toast.error('Only the talent can mark the project as completed.');
      return;
    }

    const connected = await ensureWalletConnection();
    if (!connected) return;

    // Note: signing a typed message does not require gas/ETH.

    const signerInstance = await resolveSigner();
    if (!signerInstance) {
      toast.error('Unable to access wallet signer.');
      return;
    }

    updateEscrowLoading('complete', true);
    try {
      const identifiers = buildEscrowIdentifiers();
      if (!identifiers.jobId || !identifiers.customerId || !identifiers.talentId || !identifiers.chatId) {
        toast.error('Missing project identifiers. Please refresh the page and try again.');
        return;
      }

      // Log current job status
      console.log('=== Escrow Completion Attempt ===');
      console.log('Chat ID:', id);
      console.log('Current workflow status:', chat.workflowStatus);
      console.log('Deposit txHash:', chat.escrow?.deposit?.txHash);
      console.log('Job ID:', identifiers.jobId);
      console.log('Customer ID:', identifiers.customerId);
      console.log('Talent ID:', identifiers.talentId);
      console.log('Talent Wallet:', walletAddress);
      console.log('==================================');

      const { relayed, txHash } = await completeEscrow({
        signer: signerInstance,
        jobId: identifiers.jobId,
        customerId: identifiers.customerId,
        talentId: identifiers.talentId,
        talentWallet: walletAddress,
        chatId: identifiers.chatId
      });
      if (!relayed || !txHash) {
        throw new Error('Relayer did not return a transaction hash');
      }
      console.log('Relayed completion submitted:', txHash);

      // Verify the job status was updated to 3 (Completed) after the transaction
      console.log('Verifying job status after completion...');
      let statusVerified = false;
      try {
        const { verifyJobStatus } = await import('../config/Escrowintegration');
        // Wait a bit for the block to be processed (sometimes needed for state updates)
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const statusCheck = await verifyJobStatus({
          signer: signerInstance,
          jobId: identifiers.jobId,
          expectedStatus: '3'
        });
        
        if (statusCheck.matches) {
          console.log('✓ Job status verified: Status is 3 (Completed)');
          statusVerified = true;
        } else {
          console.error('✗ Job status verification failed after completion:', {
            expected: '3',
            actual: statusCheck.status,
            jobId: identifiers.jobId
          });
          // Try again after a longer wait
          await new Promise(resolve => setTimeout(resolve, 2000));
          const retryCheck = await verifyJobStatus({
            signer: signerInstance,
            jobId: identifiers.jobId,
            expectedStatus: '3'
          });
          if (retryCheck.matches) {
            console.log('✓ Job status verified on retry: Status is 3 (Completed)');
            statusVerified = true;
          } else {
            console.error('✗ Job status still not updated after retry:', {
              expected: '3',
              actual: retryCheck.status
            });
          }
        }
      } catch (verifyError) {
        console.warn('Could not verify job status after completion:', verifyError);
        // Don't fail the flow - transaction succeeded, so state should be updated
        // The verification might fail due to network issues, but the transaction succeeded
      }

      if (!statusVerified) {
        console.warn('Job status verification inconclusive, but transaction succeeded. Proceeding with backend update.');
        console.warn('If confirmation fails, the job status may need to be checked again.');
      }

      // Only call backend after successful on-chain transaction
      // Include the identifiers so backend can verify they match the deposit
      await api.post(API_ENDPOINTS.CHATS.ESCROW_COMPLETE(id), {
        txHash,
        fromAddress: walletAddress,
        // Include identifiers so backend can verify consistency
        jobId: identifiers.jobId,
        customerId: identifiers.customerId,
        talentId: identifiers.talentId,
        chatId: identifiers.chatId
      });

      console.log('Backend updated successfully');
      toast.success('Work completion recorded successfully.');
      invalidateChatData();
    } catch (error) {
      console.error('Completion error:', error);
      const serverMessage = error?.response?.data?.error || error?.message || 'Completion failed';
      const details = error?.response?.data?.details;
      toast.error(details ? `${serverMessage}: ${details}` : serverMessage);
    } finally {
      updateEscrowLoading('complete', false);
    }
  };

  const handleEscrowConfirm = async () => {
    if (!chat) return;

    if (!clientParticipant || clientParticipant.user._id !== user?._id) {
      toast.error('Only the client can confirm the project.');
      return;
    }

    if (!talentParticipant || !talentParticipant.user?._id) {
      toast.error('Talent participant information is missing.');
      return;
    }

    // First, check if wallet is connected
    const connected = await ensureWalletConnection();
    if (!connected) {
      toast.error('Please connect your wallet first.');
      return;
    }

    // After wallet is connected, check if it matches the deposit wallet
    const depositWallet = chat.escrow?.deposit?.fromAddress;
    if (depositWallet && normalizeAddress(depositWallet) !== normalizeAddress(walletAddress)) {
      toast.error(
        `Please switch to the wallet used for deposit (${displayAddress(depositWallet)}).`
      );
      return;
    }

    const usdAmount = determineUsdAmount();
    if (!usdAmount || Number.isNaN(usdAmount) || usdAmount <= 0) {
      toast.error('Unable to determine escrow amount.');
      return;
    }

    // Note: signing a typed message does not require gas/ETH.

    const signerInstance = await resolveSigner();
    if (!signerInstance) {
      toast.error('Unable to access wallet signer.');
      return;
    }

    updateEscrowLoading('confirm', true);
    try {
      const identifiers = buildEscrowIdentifiers();
      if (!identifiers.jobId || !identifiers.customerId || !identifiers.talentId || !identifiers.chatId) {
        toast.error('Missing project identifiers. Please refresh the page and try again.');
        return;
      }

      // Backend still stores the payment token amount in `amountETH` field name.
      let amountETH = chat.escrow?.deposit?.amountETH || 0;

      // Log current job status
      console.log('=== Escrow Confirmation Attempt ===');
      console.log('Chat ID:', id);
      console.log('Current workflow status:', chat.workflowStatus);
      console.log('Deposit txHash:', chat.escrow?.deposit?.txHash);
      console.log('Completion txHash:', chat.escrow?.completion?.txHash);
      console.log('Confirmation txHash (if exists):', chat.escrow?.confirmation?.txHash);
      console.log('Job ID:', identifiers.jobId);
      console.log('Customer ID:', identifiers.customerId);
      console.log('Talent ID:', identifiers.talentId);
      console.log('Customer Wallet:', walletAddress);
      console.log('Deposit Wallet:', chat.escrow?.deposit?.fromAddress);
      console.log('USD Amount:', usdAmount);
      console.log('USDC Amount (stored as amountETH field):', amountETH);
      console.log('====================================');

      // Validate backend state before attempting confirmation
      if (!chat.escrow?.completion?.txHash) {
        toast.error('Completion transaction not found. Please wait for the talent to complete the work.');
        return;
      }

      // Allow confirmation even if workflowStatus is not 'completed' if we have completion txHash
      // This handles the case where the contract state is ahead of the backend
      if (chat.workflowStatus !== 'completed' && !chat.escrow?.completion?.txHash) {
        toast.error(`Workflow status is "${chat.workflowStatus}". Expected: "completed". Please refresh the page or wait for the backend to sync.`);
        console.error('Backend workflow status mismatch:', {
          current: chat.workflowStatus,
          expected: 'completed',
          completionTxHash: chat.escrow?.completion?.txHash
        });
        // Refresh chat data to get latest state
        invalidateChatData();
        return;
      }
      
      // If workflowStatus is not 'completed' but we have completion txHash, log a warning but proceed
      if (chat.workflowStatus !== 'completed' && chat.escrow?.completion?.txHash) {
        console.warn('Workflow status is not "completed" but completion txHash exists. Proceeding with confirmation to sync backend state.');
      }

      // Check if already confirmed
      if (chat.escrow?.confirmation?.txHash) {
        toast.success('This job is already confirmed. Status is up to date.');
        invalidateChatData();
        return;
      }

      // Before calling confirm, verify the job status on-chain
      // This helps catch cases where completion didn't update the status properly
      console.log('Verifying job status before confirmation...');
      try {
        const { verifyJobStatus } = await import('../config/Escrowintegration');
        const preConfirmCheck = await verifyJobStatus({
          signer: signerInstance,
          jobId: identifiers.jobId,
          expectedStatus: '2'
        });
        
        console.log('Pre-confirmation status check:', {
          status: preConfirmCheck.status,
          expected: '3',
          matches: preConfirmCheck.matches,
          jobId: identifiers.jobId
        });
        
        if (!preConfirmCheck.matches && (preConfirmCheck.status === '1' || preConfirmCheck.status === '2')) {
          // Job is still in status 1 (Deposited) or 2 (In-progress), not 3 (Completed)
          // This means the completion transaction may not have updated the status
          const statusName = preConfirmCheck.status === '1' ? 'Deposited' : 'In-progress';
          console.error(`Job is still in status ${preConfirmCheck.status} (${statusName}). Completion may not have updated the status.`);
          console.error('This could indicate:');
          console.error('1. The completion transaction didn\'t actually update the contract');
          console.error('2. The jobId, customerId, or talentId don\'t match what was used during completion');
          console.error('3. There\'s a timing issue and we need to wait longer');
          
          // Check if we have a completion txHash - if so, the transaction succeeded
          if (chat.escrow?.completion?.txHash) {
            console.warn(`Completion txHash exists, but job status is still ${preConfirmCheck.status}. This is unusual.`);
            console.warn('Attempting to refresh chat data and retry status check...');
            
            // Refresh chat data in case backend has updated
            invalidateChatData();
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Retry the status check
            const retryCheck = await verifyJobStatus({
              signer: signerInstance,
              jobId: identifiers.jobId,
              expectedStatus: '3'
            });
            
            if (!retryCheck.matches) {
              toast.error(`Cannot confirm: Job status is still ${retryCheck.status} instead of 3 (Completed). The completion transaction may not have updated the contract state correctly. Please check the completion transaction and try again.`);
              console.error('Status check failed after retry:', retryCheck);
              return;
            } else {
              console.log('Status check passed on retry:', retryCheck);
            }
          } else {
            toast.error('Completion transaction not found. Please wait for the talent to complete the work.');
            return;
          }
        }
      } catch (verifyError) {
        console.warn('Could not verify job status before confirmation:', verifyError);
        // Continue - the confirmEscrow function will check the status too
      }

      console.log('Calling confirmEscrow...');
      const confirmResult = await confirmEscrow({
        signer: signerInstance,
        jobId: identifiers.jobId,
        customerId: identifiers.customerId,
        talentId: identifiers.talentId,
        customerWallet: walletAddress,
        chatId: identifiers.chatId
      });

      console.log('confirmEscrow result:', confirmResult);

      // Check if contract is already confirmed
      if (confirmResult?.alreadyConfirmed) {
        console.log('Contract is already confirmed on-chain (status: 3).');
        console.log('Checking if backend has the confirmation txHash...');
        
        // If backend already has confirmation txHash, we're done
        if (chat?.escrow?.confirmation?.txHash) {
          console.log('Backend already has confirmation txHash:', chat.escrow.confirmation.txHash);
          toast.success('Job is already confirmed. Status is up to date.');
          invalidateChatData();
          return;
        }
        
        // Contract is confirmed but backend doesn't have the txHash
        // This means the transaction succeeded but backend update failed
        // We need to find the confirmation transaction and sync it
        console.warn('Contract is confirmed but backend is not synced. Need to find confirmation txHash.');
        
        // Show a helpful message
        toast.error('Contract is already confirmed on-chain, but the backend is not synchronized. Please check your wallet activity for the confirmation transaction and note the transaction hash. You may need to contact support to sync the backend state.');
        
        // Refresh to see if backend has updated
        invalidateChatData();
        return;
      }

      if (!confirmResult?.relayed || !confirmResult?.txHash) {
        console.error('Invalid confirmResult:', confirmResult);
        throw new Error('Relayer did not return a transaction hash for confirmation');
      }

      const txHash = confirmResult.txHash;
      console.log('Relayed confirmation submitted:', txHash);

      // Only call backend after successful on-chain transaction
      // Always include amounts - use deposit amounts as fallback if not available
      const depositUSD = chat.escrow?.deposit?.amountUSD || 0;
      const depositETH = chat.escrow?.deposit?.amountETH || 0; // now carries USDC amount (backend field name)
      
      // Resolve talent wallet - don't send null
      const resolvedTalentWallet = chat.escrow?.deposit?.toAddress || 
                                    chat.escrow?.completion?.fromAddress || 
                                    resolveTalentWallet();
      
      // Build payload - only include valid, non-undefined values
      const finalUSD = (usdAmount && !isNaN(usdAmount) && usdAmount > 0) ? Number(usdAmount) : (depositUSD > 0 ? Number(depositUSD) : null);
      const finalETH = (depositETH > 0 ? Number(depositETH) : null);
      
      // Build payload object - only add fields with valid values
          const confirmPayload = {
            txHash: String(txHash),
            fromAddress: String(walletAddress),
            // Always include identifiers so backend can verify consistency
            jobId: identifiers.jobId,
            customerId: identifiers.customerId,
            talentId: identifiers.talentId,
            chatId: identifiers.chatId
          };
          
          // Add amounts - always include them if we have deposit amounts
          // Even if they're 0, include them so backend knows the amounts
          if (depositUSD > 0) {
            confirmPayload.amountUSD = finalUSD > 0 ? finalUSD : depositUSD;
          } else if (finalUSD > 0) {
            confirmPayload.amountUSD = finalUSD;
          }
          
          if (depositETH > 0) {
            confirmPayload.amountETH = finalETH > 0 ? finalETH : depositETH;
          } else if (finalETH > 0) {
            confirmPayload.amountETH = finalETH;
          }
          
          // Only include talentWallet if we have a valid, non-null value
          if (resolvedTalentWallet && 
              resolvedTalentWallet !== 'null' && 
              resolvedTalentWallet !== null && 
              resolvedTalentWallet !== undefined &&
              resolvedTalentWallet !== '' &&
              typeof resolvedTalentWallet === 'string') {
            confirmPayload.talentWallet = String(resolvedTalentWallet);
          }
          
          console.log('Sending confirmation payload:', JSON.stringify(confirmPayload, null, 2));
          console.log('Payload keys:', Object.keys(confirmPayload));
          console.log('Payload values:', Object.values(confirmPayload));
          console.log('Identifiers:', {
            jobId: identifiers.jobId,
            customerId: identifiers.customerId,
            talentId: identifiers.talentId,
            chatId: identifiers.chatId
          });
          console.log('Amounts - USD:', finalUSD, 'USDC:', finalETH);
          console.log('Talent wallet:', resolvedTalentWallet || 'not provided (backend will resolve)');
          
          // Call backend to record confirmation
          await api.post(API_ENDPOINTS.CHATS.ESCROW_CONFIRM(id), confirmPayload);

      console.log('Backend updated successfully');
      toast.success('Escrow confirmed and released successfully.');
      invalidateChatData();
    } catch (error) {
      console.error('Confirmation error:', error);
      
      // Check if this is a validation error from backend
      if (error?.response?.status === 400) {
        const errorData = error.response.data;
        console.error('Backend validation error:', errorData);
        
        // Check if transaction succeeded on-chain but backend validation failed
        // In this case, we might need to retry with correct data or sync
        if (errorData?.errors && Array.isArray(errorData.errors)) {
          const validationErrors = errorData.errors.map(e => `${e.param || 'unknown'}: ${e.msg || e}`).join(', ');
          console.error('Validation errors:', validationErrors);
          
          // Get the txHash from the receipt if available
          const txHash = error.config?.data ? JSON.parse(error.config.data).txHash : null;
          
          // Retry with minimal payload - backend should resolve missing fields
          if (txHash) {
            console.log('Retrying confirmation with minimal payload...');
            const retryPayload = {
              txHash,
              fromAddress: walletAddress
            };
            
            // Only include amounts if we have valid deposit amounts
            if (chat.escrow?.deposit?.amountUSD > 0) {
              retryPayload.amountUSD = chat.escrow.deposit.amountUSD;
            }
            if (chat.escrow?.deposit?.amountETH > 0) {
              retryPayload.amountETH = chat.escrow.deposit.amountETH;
            }
            
            try {
              await api.post(API_ENDPOINTS.CHATS.ESCROW_CONFIRM(id), retryPayload);
              console.log('Backend updated successfully on retry');
              toast.success('Escrow confirmed and released successfully.');
              invalidateChatData();
              return;
            } catch (retryError) {
              console.error('Retry failed:', retryError);
              toast.error(`Validation error: ${validationErrors}. Please refresh the page and try again.`);
              return;
            }
          } else {
            toast.error(`Validation error: ${validationErrors}. Please refresh and try again.`);
            return;
          }
        } else {
          // Not a validation error array, show the error message
          const errorMsg = errorData?.error || errorData?.message || 'Backend validation failed';
          const details = errorData?.details;
          toast.error(details ? `${errorMsg}: ${details}` : errorMsg);
          return;
        }
      }
      
      // Check if job is already confirmed on-chain
      if (error?.message && error.message.includes('already confirmed')) {
        console.warn('Job is already confirmed on-chain. Attempting to sync backend state...');
        
        // Check if backend already has confirmation txHash
        if (chat?.escrow?.confirmation?.txHash) {
          toast.success('Job is already confirmed. Status is up to date.');
          invalidateChatData();
          return;
        }
        
        // If backend doesn't have confirmation txHash, try to sync it
        // The transaction succeeded, so we should have the txHash in scope
        console.warn('Contract is already confirmed but backend is not synced. Attempting to sync...');
        
        // Refresh chat data - backend might have synced
        invalidateChatData();
        toast.warning('Contract is already confirmed on-chain. Refreshing to sync backend state...');
        return;
      }
      
      // Check if job is not in completed state
      if (error?.message && error.message.includes('not in completed state')) {
        toast.error(error.message || 'Job is not in completed state. Please wait for the completion transaction to be confirmed.');
        return;
      }
      
      // Log the full error response for debugging
      if (error?.response?.data) {
        console.error('Backend error response:', error.response.data);
      }
      
      const serverMessage = error?.response?.data?.error || error?.message || 'Confirmation failed';
      const details = error?.response?.data?.details;
      const errors = error?.response?.data?.errors; // Validation errors
      
      if (errors && Array.isArray(errors)) {
        const errorMessages = errors.map(e => `${e.param || 'unknown'}: ${e.msg || e}`).join(', ');
        toast.error(`Validation error: ${errorMessages}`);
      } else {
        toast.error(details ? `${serverMessage}: ${details}` : serverMessage);
      }
    } finally {
      updateEscrowLoading('confirm', false);
    }
  };

  const getWorkflowSteps = () => {
    return [
      { id: 'offered', label: 'Offered', icon: DollarSign },
      { id: 'deposit', label: 'Deposit', icon: Clock },
      { id: 'in-progress', label: 'In Progress', icon: AlertCircle },
      { id: 'completed', label: 'Completed', icon: CheckCircle },
      { id: 'confirmed', label: 'Confirmed', icon: CheckCircle }
    ];
  };

  const getWorkflowStepStatus = (stepId) => {
    if (stepId === 'deposit' && chat.escrow?.deposit?.txHash) {
      return 'completed';
    }
    if (stepId === 'in-progress' && chat.escrow?.inProgress?.txHash) {
      return 'completed';
    }
    if (stepId === 'in-progress' && chat.workflowStatus === 'deposit' && chat.escrow?.deposit?.txHash) {
      return 'current';
    }

    const currentStepIndex = getWorkflowSteps().findIndex(step => step.id === chat.workflowStatus);
    const stepIndex = getWorkflowSteps().findIndex(step => step.id === stepId);
    
    if (stepIndex < currentStepIndex) return 'completed';
    if (stepIndex === currentStepIndex) return 'current';
    return 'pending';
  };

  const canUserPerformAction = (action) => {
    const userRole = user?.role;
    const currentStatus = chat.workflowStatus;
    
    if (action === 'deposit' && chat.escrow?.deposit?.txHash) {
      return false;
    }

    if (action === 'in-progress' && chat.escrow?.inProgress?.txHash) {
      return false;
    }

    if (action === 'completed' && chat.escrow?.completion?.txHash) {
      return false;
    }

    if (action === 'confirmed' && chat.escrow?.confirmation?.txHash) {
      return false;
    }

    // For 'confirmed' action, only the person who made the deposit can confirm
    if (action === 'confirmed') {
      // This is a simplified check - in a real app you'd track who made the deposit
      // For now, we'll allow the client to confirm after talent completes
      return userRole === 'client' && currentStatus === 'completed';
    }
    
    const permissions = {
      client: {
        // When a job is offered the client should be able to deposit
        offered: ['deposit'],
        // After deposit the client shouldn't be able to deposit again
        deposit: [],
        // While in-progress the client can disburse funds
        'in-progress': ['disburse'],
        // After talent completes, client can confirm
        completed: ['confirmed']
      },
      talent: {
        // Talent progresses the workflow after deposit
        deposit: ['in-progress'],
        // Talent marks in-progress -> completed
        'in-progress': ['completed'],
        // Talent cannot confirm
        completed: []
      }
    };
    
    return permissions[userRole]?.[currentStatus]?.includes(action) || false;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  // Helper function to parse and render markdown-style formatting
  const parseMarkdown = (text, isOwnMessage = false) => {
    if (!text) return null;
    
    // Process text line by line to handle formatting
    const lines = text.split('\n');
    
    return lines.map((line, lineIndex) => {
      if (!line.trim()) {
        return <br key={lineIndex} />;
      }
      
      const lineParts = [];
      let lastBoldIndex = 0;
      
      // Process bold text (**text**)
      const boldRegex = /\*\*(.+?)\*\*/g;
      let boldMatch;
      
      while ((boldMatch = boldRegex.exec(line)) !== null) {
        // Add text before bold
        if (boldMatch.index > lastBoldIndex) {
          const beforeText = line.substring(lastBoldIndex, boldMatch.index);
          // Also check for URLs in the text before bold
          const urlRegex = /(https?:\/\/[^\s]+)/g;
          let urlMatch;
          let urlLastIndex = 0;
          const beforeParts = [];
          
          while ((urlMatch = urlRegex.exec(beforeText)) !== null) {
            if (urlMatch.index > urlLastIndex) {
              beforeParts.push(beforeText.substring(urlLastIndex, urlMatch.index));
            }
            beforeParts.push(
              <a
                key={`url-${lineIndex}-${urlMatch.index}`}
                href={urlMatch[0]}
                target="_blank"
                rel="noopener noreferrer"
                className={`underline hover:opacity-80 ${isOwnMessage ? 'text-blue-100' : 'text-blue-600 dark:text-blue-400'}`}
              >
                {urlMatch[0]}
              </a>
            );
            urlLastIndex = urlMatch.index + urlMatch[0].length;
          }
          if (urlLastIndex < beforeText.length) {
            beforeParts.push(beforeText.substring(urlLastIndex));
          }
          lineParts.push(...(beforeParts.length > 0 ? beforeParts : [beforeText]));
        }
        
        // Add bold text
        lineParts.push(
          <strong key={`bold-${lineIndex}-${boldMatch.index}`} className="font-semibold">
            {boldMatch[1]}
          </strong>
        );
        
        lastBoldIndex = boldMatch.index + boldMatch[0].length;
      }
      
      // Add remaining text after last bold
      if (lastBoldIndex < line.length) {
        const afterText = line.substring(lastBoldIndex);
        // Check for URLs in remaining text
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        let urlMatch;
        let urlLastIndex = 0;
        const afterParts = [];
        
        while ((urlMatch = urlRegex.exec(afterText)) !== null) {
          if (urlMatch.index > urlLastIndex) {
            afterParts.push(afterText.substring(urlLastIndex, urlMatch.index));
          }
          afterParts.push(
            <a
              key={`url-${lineIndex}-${urlMatch.index}`}
              href={urlMatch[0]}
              target="_blank"
              rel="noopener noreferrer"
              className={`underline hover:opacity-80 ${isOwnMessage ? 'text-blue-100' : 'text-blue-600 dark:text-blue-400'}`}
            >
              {urlMatch[0]}
            </a>
          );
          urlLastIndex = urlMatch.index + urlMatch[0].length;
        }
        if (urlLastIndex < afterText.length) {
          afterParts.push(afterText.substring(urlLastIndex));
        }
        lineParts.push(...(afterParts.length > 0 ? afterParts : [afterText]));
      }
      
      // Check if line starts with bullet point
      if (line.trim().startsWith('- ')) {
        return (
          <div key={lineIndex} className="flex items-start my-1">
            <span className="mr-2 mt-0.5">•</span>
            <span className="flex-1">{lineParts.length > 0 ? lineParts : line.substring(2)}</span>
          </div>
        );
      }
      
      return (
        <div key={lineIndex} className="my-1">
          {lineParts.length > 0 ? lineParts : line}
        </div>
      );
    });
  };

  // Helper function to detect and render links
  const renderMessageContent = (content, isOwnMessage = false) => {
    if (!content) return null;
    
    // Use markdown parser which also handles URLs
    return <div className="space-y-1">{parseMarkdown(content, isOwnMessage)}</div>;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Download file handler - uses backend proxy to avoid CORS issues
  const handleDownload = async (url, filename, messageId, attachmentIndex) => {
    try {
      // If we have messageId and attachmentIndex, use the proxy endpoint with axios (includes auth)
      if (messageId && attachmentIndex !== undefined && attachmentIndex >= 0) {
        try {
          const response = await api.get(API_ENDPOINTS.CHATS.DOWNLOAD(messageId, attachmentIndex), {
            responseType: 'blob' // Important: tells axios to handle binary data
          });
          
          // Create blob URL and trigger download
          const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = filename || 'download';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
          return;
        } catch (apiError) {
          console.error('Download via API error:', apiError);
          toast.error('Failed to download file via API. Trying direct download...');
          // Fall through to direct download
        }
      }

      // Fallback: try direct download (may fail due to CORS)
      const response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch file');
      }
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file. Opening in new tab...');
      window.open(url, '_blank');
    }
  };

  // Helper functions for quick access items
  const extractLinksFromMessages = () => {
    const links = [];
    messages.forEach(message => {
      if (message.type === 'text' && message.content) {
        // Simple URL regex to find links in text
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const matches = message.content.match(urlRegex);
        if (matches) {
          matches.forEach(url => {
            links.push({
              url,
              messageId: message._id,
              timestamp: message.timestamp,
              sender: message.senderId?.username || (message.senderId?._id ? 'User' : 'Unknown')
            });
          });
        }
      }
    });
    return links;
  };

  const extractImagesFromMessages = () => {
    const images = [];
    messages.forEach(message => {
      if (message.attachments && message.attachments.length > 0) {
        message.attachments.forEach((attachment, attIndex) => {
          if (attachment.mimeType && attachment.mimeType.startsWith('image/')) {
            images.push({
              url: attachment.url,
              filename: attachment.originalName || attachment.filename || 'image',
              messageId: message._id,
              attachmentIndex: attIndex,
              timestamp: message.timestamp,
              sender: message.senderId.username || (message.senderId._id ? 'User' : 'Unknown'),
              attachment
            });
          }
        });
      } else if (message.type === 'image' && message.content) {
        // Legacy support for old image messages
        images.push({
          url: message.content,
          filename: 'image',
          messageId: message._id,
          attachmentIndex: -1,
          timestamp: message.timestamp,
          sender: message.senderId.username || (message.senderId._id ? 'User' : 'Unknown'),
          attachments: message.attachments || []
        });
      }
    });
    return images;
  };

  const extractFilesFromMessages = () => {
    const files = [];
    messages.forEach(message => {
      if (message.attachments && message.attachments.length > 0) {
        message.attachments.forEach((attachment, attIndex) => {
          if (!attachment.mimeType || !attachment.mimeType.startsWith('image/')) {
            files.push({
              url: attachment.url,
              filename: attachment.originalName || attachment.filename || 'file',
              messageId: message._id,
              attachmentIndex: attIndex,
              timestamp: message.timestamp,
              sender: message.senderId.username || (message.senderId._id ? 'User' : 'Unknown'),
              attachment
            });
          }
        });
      } else if (message.type === 'file' && message.content) {
        // Legacy support for old file messages
        files.push({
          url: message.content,
          filename: 'file',
          messageId: message._id,
          attachmentIndex: -1,
          timestamp: message.timestamp,
          sender: message.senderId.username || (message.senderId._id ? 'User' : 'Unknown'),
          attachments: message.attachments || []
        });
      }
    });
    return files;
  };

  const getQuickAccessItems = () => {
    switch (quickAccessFilter) {
      case 'links':
        return extractLinksFromMessages();
      case 'images':
        return extractImagesFromMessages();
      case 'files':
        return extractFilesFromMessages();
      default:
        return [
          ...extractLinksFromMessages().map(item => ({ ...item, type: 'link' })),
          ...extractImagesFromMessages().map(item => ({ ...item, type: 'image' })),
          ...extractFilesFromMessages().map(item => ({ ...item, type: 'file' }))
        ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'offered': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'deposit': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'in-progress': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'confirmed': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'offered': return <DollarSign className="w-4 h-4" />;
      case 'deposit': return <Clock className="w-4 h-4" />;
      case 'in-progress': return <AlertCircle className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'confirmed': return <CheckCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="p-6">
        <div className="text-center py-16">
          <div className="text-6xl mb-4">💬</div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Chat not found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The chat you're looking for doesn't exist or has been removed.
          </p>
          <button 
            onClick={() => navigate('/chats')}
            className="btn-primary"
          >
            Back to Chats
          </button>
        </div>
      </div>
    );
  }

  const otherParticipant = chat.participants.find(p => p.user._id !== user?.id);
  const isClient = user?.role === 'client';
  const isTalent = user?.role === 'talent';

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Chat Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3 sm:p-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
            <button 
              onClick={() => navigate('/chats')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-accent rounded-full flex items-center justify-center text-white font-medium text-sm sm:text-base flex-shrink-0">
              {otherParticipant?.user?.username?.[0].toUpperCase()}
            </div>
            
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm sm:text-base truncate">
                {otherParticipant?.user?.username}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                {(() => {
                  // Check if chat.job exists and has a title property
                  if (chat.job) {
                    const jobTitle = typeof chat.job === 'object' ? chat.job.title : null;
                    if (jobTitle) return jobTitle;
                    // If job exists but no title, it might be an ID - return fallback
                    return 'Job Project';
                  }
                  // Check if chat.gig exists and has a title property
                  if (chat.gig) {
                    const gigTitle = typeof chat.gig === 'object' ? chat.gig.title : null;
                    if (gigTitle) return gigTitle;
                    // If gig exists but no title, it might be an ID - return fallback
                    return 'Gig Service';
                  }
                  return 'General Chat';
                })()}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
            <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              <Phone className="w-5 h-5" />
            </button>
            <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              <Video className="w-5 h-5" />
            </button>
            <button 
              onClick={() => setShowInfo(!showInfo)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <Info className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden relative min-h-0">
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Tab Navigation */}
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex">
              {!isAddedParticipant && !hasActiveProposal && (
                <button
                  onClick={() => setActiveTab('progress')}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === 'progress'
                      ? 'border-accent text-accent bg-accent/5 dark:bg-accent/10'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <span className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Project Progress
                  </span>
                </button>
              )}
              <button
                onClick={() => setActiveTab('chat')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === 'chat'
                    ? 'border-accent text-accent bg-accent/5 dark:bg-accent/10'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" />
                  Chat Details
                  {hasActiveProposal && proposalData?.proposalId && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/governance/proposals/${proposalData.proposalId}`);
                      }}
                      className="ml-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-accent/20 text-accent border border-accent/30 hover:bg-accent/30 cursor-pointer transition-colors"
                    >
                      In DAO
                    </button>
                  )}
                </span>
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'progress' && !hasActiveProposal ? (
            /* Project Progress Tab */
            <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-800">
              <div className="p-4 sm:p-6">
                {/* Price Display */}
                <div className="mb-4 sm:mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Project Value
                    </h3>
                    {chat.workflowStatus === 'offered' && (
                      <button
                        onClick={() => setShowPriceResetModal(true)}
                        className="flex items-center space-x-1 text-xs sm:text-sm text-accent hover:text-accent-dark transition-colors"
                      >
                        <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Reset Price</span>
                      </button>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 sm:space-x-4 flex-wrap">
                    <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
                      ${chat.price?.current || 0}
                    </div>
                    {chat.price?.original && chat.price.original !== chat.price.current && (
                      <div className="flex items-center space-x-2">
                        <span className="text-xs sm:text-sm text-gray-500 line-through">
                          ${chat.price.original}
                        </span>
                        <span className="text-xs sm:text-sm text-green-600 dark:text-green-400">
                          {chat.price.current > chat.price.original ? (
                            <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                          ) : (
                            <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4" />
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Project Preview */}
                {(chat.job || chat.gig) && (
                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          {chat.job ? 'Job Project' : 'Gig Service'}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          const jobId = chat.job?._id || chat.job;
                          const gigId = chat.gig?._id || chat.gig;
                          if (jobId) {
                            navigate(`/jobs/${jobId}`);
                          } else if (gigId) {
                            navigate(`/gigs/${gigId}`);
                          }
                        }}
                        className="flex items-center space-x-1 text-accent hover:text-accent-dark transition-colors flex-shrink-0 text-xs sm:text-sm"
                      >
                        <span className="hidden sm:inline">View Details</span>
                        <span className="sm:hidden">View</span>
                        <ExternalLinkIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Workflow Progress */}
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">
                    Project Progress
                  </h3>
                  <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto pb-2 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                    {getWorkflowSteps().map((step, index) => {
                      const status = getWorkflowStepStatus(step.id);
                      const Icon = step.icon;
                      const canPerform = canUserPerformAction(step.id);
                      const escrowStepLoading = step.id === 'deposit'
                        ? escrowLoading.deposit
                        : step.id === 'in-progress'
                          ? escrowLoading.inProgress
                          : step.id === 'completed'
                            ? escrowLoading.complete
                            : step.id === 'confirmed'
                              ? escrowLoading.confirm
                              : false;
                      
                      return (
                        <div key={step.id} className="flex items-center flex-shrink-0">
                          <button
                            onClick={async (event) => {
                              event.preventDefault();
                              if (!canPerform) {
                                toast.error(`${user?.role} cannot perform this action at current stage`);
                                return;
                              }
                              await handleWorkflowStepClick(step.id);
                            }}
                            disabled={(!canPerform && status !== 'current') || escrowStepLoading}
                            className={`
                              relative flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all duration-200
                              ${status === 'completed' 
                                ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                                : status === 'current' 
                                  ? 'bg-accent text-white shadow-lg' 
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                              }
                              ${canPerform ? 'hover:shadow-md cursor-pointer' : 'cursor-not-allowed opacity-50'}
                              ${status === 'current' ? 'ring-2 ring-accent ring-opacity-50' : ''}
                            `}
                          >
                            <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span className="text-xs sm:text-sm font-medium whitespace-nowrap">
                              {escrowStepLoading ? 'Processing...' : step.label}
                            </span>
                            {status === 'completed' && (
                              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 ml-0.5 sm:ml-1" />
                            )}
                          </button>
                          
                          {index < getWorkflowSteps().length - 1 && (
                            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mx-1 sm:mx-2 flex-shrink-0" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Progress indicator */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                      <span>Progress</span>
                      <span>{Math.round((getWorkflowSteps().findIndex(step => step.id === chat.workflowStatus) + 1) / getWorkflowSteps().length * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-accent h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${(getWorkflowSteps().findIndex(step => step.id === chat.workflowStatus) + 1) / getWorkflowSteps().length * 100}%` 
                        }}
                      />
                    </div>
                  </div>

                  {chat.escrow?.deposit?.txHash && (
                    <div className="mt-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-xs sm:text-sm text-gray-700 dark:text-gray-200">
                      <p className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                        Escrow Summary
                      </p>
                      <div className="space-y-1">
                        <p className="break-words">
                          Deposit Wallet:{' '}
                          <span className="font-mono text-xs">
                            {displayAddress(chat.escrow.deposit.fromAddress)}
                          </span>
                        </p>
                        <p>
                          Amount Locked:{' '}
                          <span className="font-semibold">
                            ${Number(chat.escrow.deposit.amountUSD || 0).toFixed(2)}
                          </span>
                          {chat.escrow.deposit.amountETH && (
                            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                              (~{Number(chat.escrow.deposit.amountETH).toFixed(6)} USDC)
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 break-all">
                          Tx Hash: <span className="font-mono">{chat.escrow.deposit.txHash}</span>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Job Details Section - Show when in-progress */}
                  {chat.workflowStatus === 'in-progress' && chat.escrow?.inProgress?.txHash && (
                    <div className="mt-4 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm sm:text-base">
                          Job Details
                        </h4>
                        <button
                          onClick={fetchJobDetailsFromBlockchain}
                          disabled={loadingJobDetails}
                          className="text-xs text-accent hover:text-accent-dark transition-colors disabled:opacity-50"
                        >
                          {loadingJobDetails ? 'Loading...' : 'Refresh'}
                        </button>
                      </div>
                      
                      {jobDetails ? (
                        <div className="space-y-2 text-xs sm:text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Total Amount:</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                              {jobDetails.totalAmountETH.toFixed(6)} USDC
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Remaining Amount:</span>
                            <span className="font-semibold text-green-600 dark:text-green-400">
                              {jobDetails.remainingAmountETH.toFixed(6)} USDC
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Client Wallet:</span>
                            <span className="font-mono text-xs">
                              {displayAddress(jobDetails.customerWallet)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Talent Wallet:</span>
                            <span className="font-mono text-xs">
                              {displayAddress(jobDetails.talentWallet)}
                            </span>
                          </div>
                          
                          {jobDetails.disbursedAmountsETH && jobDetails.disbursedAmountsETH.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                              <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                                Disbursed Amounts:
                              </p>
                              <div className="space-y-1 max-h-32 overflow-y-auto">
                                {jobDetails.disbursedAmountsETH.map((amount, index) => (
                                  <div key={index} className="flex justify-between text-xs">
                                    <span className="text-gray-500 dark:text-gray-400">
                                      Disbursement #{index + 1}:
                                    </span>
                                    <span className="font-medium text-gray-900 dark:text-gray-100">
                                      {amount.toFixed(6)} USDC
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Click "Refresh" to load job details from blockchain
                        </p>
                      )}
                    </div>
                  )}

                  {/* Disbursement Button - Only for client when in-progress */}
                  {isClient && chat.workflowStatus === 'in-progress' && chat.escrow?.inProgress?.txHash && (
                    <div className="mt-4">
                      <button
                        onClick={async () => {
                          // Fetch job details if not already loaded
                          if (!jobDetails) {
                            await fetchJobDetailsFromBlockchain();
                          }
                          // Fetch USDC price if not already loaded - use same method as useQuery
                          if (!ethPrice) {
                            try {
                              const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                              const response = await axios.get(`${API_URL}/api/v1/price/usdc`);
                              if (response.data?.usd) {
                                setEthPrice(response.data.usd);
                              }
                            } catch (error) {
                              console.error('Failed to fetch USDC price:', error);
                            }
                          }
                          setShowDisburseModal(true);
                        }}
                        className="w-full px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark transition-colors font-medium"
                      >
                        Disburse Funds
                      </button>
                    </div>
                  )}
                </div>

                {/* Status Badge */}
                <div className="flex items-center justify-center">
                  <span className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(chat.workflowStatus)}`}>
                    {getStatusIcon(chat.workflowStatus)}
                    <span className="ml-1 sm:ml-2 capitalize">{chat.workflowStatus.replace('-', ' ')}</span>
                  </span>
                </div>
              </div>
            </div>
          ) : (
            /* Chat Details Tab */
            <div className="flex-1 flex flex-col min-h-0">
              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4 min-h-0">
            {messages.map((message, index) => {
              // Better user ID comparison for message ownership
              const messageSenderId = message.senderId._id || message.senderId;
              const currentUserId = user?._id || user?.id;
              const isOwn = messageSenderId.toString() === currentUserId.toString();
              const showDate = index === 0 || 
                formatDate(messages[index - 1].timestamp) !== formatDate(message.timestamp);

              return (
                <div key={message._id}>
                  {showDate && (
                    <div className="text-center text-sm text-gray-500 dark:text-gray-400 my-4">
                      {formatDate(message.timestamp)}
                    </div>
                  )}
                  
                  <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`}>
                    <div className={`flex flex-col max-w-[85%] sm:max-w-xs lg:max-w-md ${
                      isOwn ? 'items-end' : 'items-start'
                    }`}>
                      {!isOwn && (
                        <div className="flex items-center space-x-2 mb-1">
                          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center text-xs font-medium">
                            {message.senderId.username[0].toUpperCase()}
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {message.senderId.username}
                          </span>
                        </div>
                      )}
                      <div className={`px-3 py-2 sm:px-4 sm:py-3 rounded-2xl shadow-sm max-w-full ${
                        isOwn 
                          ? 'bg-blue-500 text-white rounded-br-md ml-auto' 
                          : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-md border border-gray-200 dark:border-gray-600'
                      }`}>
                      {/* Message Content */}
                      {message.content && (
                        <div className="text-sm mb-2">
                          {renderMessageContent(message.content, isOwn)}
                        </div>
                      )}
                      
                      {/* Attachments */}
                      {message.attachments && message.attachments.length > 0 && (
                        <div className="space-y-2 mt-2">
                          {message.attachments.map((attachment, attIndex) => {
                            const isImage = attachment.mimeType?.startsWith('image/');
                            
                            return (
                              <div key={attIndex} className="space-y-1">
                                {isImage ? (
                                  <div className="relative group">
                                    <img 
                                      src={attachment.url} 
                                      alt={attachment.originalName || 'Attachment'} 
                                      className="rounded-lg max-w-full h-auto max-h-64 cursor-pointer"
                                      onClick={() => window.open(attachment.url, '_blank')}
                                    />
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownload(attachment.url, attachment.originalName || 'image', message._id, attIndex);
                                      }}
                                      className="absolute top-2 right-2 p-2 bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                      title="Download"
                                    >
                                      <Download className="w-4 h-4 text-white" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className={`flex items-center space-x-3 p-3 rounded-lg border ${
                                    isOwn 
                                      ? 'bg-blue-400 bg-opacity-20 border-blue-300' 
                                      : 'bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500'
                                  }`}>
                                    <File className="w-5 h-5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium truncate">
                                        {attachment.originalName || attachment.filename || 'File'}
                                      </p>
                                      {attachment.size && (
                                        <p className="text-xs opacity-75">
                                          {formatFileSize(attachment.size)}
                                        </p>
                                      )}
                                    </div>
                                    <button
                                      onClick={() => handleDownload(attachment.url, attachment.originalName || 'file', message._id, attIndex)}
                                      className="p-2 hover:bg-opacity-20 rounded-full transition-colors"
                                      title="Download"
                                    >
                                      <Download className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                        <p className={`text-xs mt-2 ${
                          isOwn ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {formatTime(message.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-3 sm:p-4 flex-shrink-0">
                {/* Selected Files Preview */}
                {selectedFiles.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {selectedFiles.map((fileObj, index) => (
                      <div key={index} className="relative inline-block">
                        {fileObj.preview ? (
                          <div className="relative">
                            <img 
                              src={fileObj.preview} 
                              alt={fileObj.name}
                              className="w-16 h-16 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                            />
                            <button
                              onClick={() => removeSelectedFile(index)}
                              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="relative flex items-center space-x-2 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
                            <File className="w-4 h-4" />
                            <span className="text-xs truncate max-w-[100px]">{fileObj.name}</span>
                            <button
                              onClick={() => removeSelectedFile(index)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <form onSubmit={handleSendMessage} className="flex items-end space-x-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex-shrink-0"
                    title="Attach file"
                  >
                    <Paperclip className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    multiple
                    accept="image/*,application/pdf,.doc,.docx,.txt,.zip"
                  />
                  
                  <div className="flex-1 relative min-w-0">
                    <textarea
                      value={messageContent}
                      onChange={(e) => setMessageContent(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      placeholder="Type a message... (Shift+Enter for new line)"
                      rows={1}
                      className="w-full input-field pr-8 sm:pr-10 text-sm sm:text-base resize-none min-h-[40px] max-h-[120px] overflow-y-auto"
                      style={{
                        height: 'auto',
                        minHeight: '40px',
                        maxHeight: '120px'
                      }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                      }}
                    />
                    <button
                      type="button"
                      className="absolute right-2 bottom-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      title="Emoji"
                    >
                      <Smile className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={(!messageContent.trim() && selectedFiles.length === 0)}
                    className="btn-primary p-2 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>

        {/* Chat Info Sidebar */}
        {showInfo && (
          <>
            {/* Mobile overlay - only on small screens (below md) */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
              onClick={() => setShowInfo(false)}
            />
            {/* Sidebar */}
            <div className="w-full md:w-80 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col h-full fixed md:relative right-0 top-0 bottom-0 z-50 md:z-auto flex-shrink-0">
              {/* Sidebar Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  Chat Information
                </h3>
                <button
                  onClick={() => setShowInfo(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg md:hidden"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Sidebar Content - Scrollable */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Participants
                      </h4>
                      {isOwner && (
                        <button
                          onClick={() => setShowAddParticipant(!showAddParticipant)}
                          className="text-xs text-accent hover:text-accent/80 flex items-center gap-1"
                        >
                          <span>+</span> Add
                        </button>
                      )}
                    </div>
                    
                    {/* Add Participant Section */}
                    {showAddParticipant && isOwner && (
                      <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search users..."
                            value={participantSearch}
                            onChange={(e) => setParticipantSearch(e.target.value)}
                            className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent"
                          />
                        </div>
                        {isSearching && (
                          <p className="text-xs text-gray-500 mt-2">Searching...</p>
                        )}
                        {searchResults.length > 0 && (
                          <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                            {searchResults.map((user) => (
                              <div
                                key={user._id}
                                className="flex items-center justify-between p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer"
                                onClick={() => handleAddParticipant(user._id)}
                              >
                                <div className="flex items-center space-x-2">
                                  <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center text-white text-xs">
                                    {user.username[0].toUpperCase()}
                                  </div>
                                  <span className="text-sm text-gray-900 dark:text-gray-100">
                                    {user.username}
                                  </span>
                                </div>
                                <button className="text-xs text-accent">Add</button>
                              </div>
                            ))}
                          </div>
                        )}
                        {participantSearch && !isSearching && searchResults.length === 0 && (
                          <p className="text-xs text-gray-500 mt-2">No users found</p>
                        )}
                      </div>
                    )}

                    <div className="space-y-2">
                      {chat.participants.map((participant) => {
                        const participantUserId = participant.user._id || participant.user;
                        const isCurrentUser = participantUserId === user?._id;
                        // Only allow removal if: user is owner OR user added this participant
                        const addedByCurrentUser = participant.addedBy && 
                          (participant.addedBy._id === user?._id || participant.addedBy === user?._id);
                        const canRemove = !isCurrentUser && 
                          participant.participantType === 'added' && 
                          (isOwner || addedByCurrentUser);
                        
                        return (
                          <div key={participantUserId} className="flex items-center justify-between group">
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-white text-sm flex-shrink-0">
                                {participant.user.username?.[0]?.toUpperCase() || 'U'}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {participant.user.username}
                                  {isCurrentUser && ' (You)'}
                                </p>
                                <div className="flex items-center gap-2">
                                  <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                    {participant.role}
                                  </p>
                                  {participant.participantType === 'added' && (
                                    <span className="text-xs text-gray-400 dark:text-gray-500">
                                      • Added
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {canRemove && (
                              <button
                                onClick={() => handleRemoveParticipant(participantUserId, participant)}
                                className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:text-red-700 transition-opacity"
                                title="Remove participant"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

              {chat.job && (
                <div>
                  <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                    Job Details
                  </h4>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {chat.job.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {chat.job.type} • ${chat.job.budget?.fixed || 'Negotiable'}
                      </p>
                    </div>
                    
                    {(chat.job.application?.coverLetter || chat.job.application?.estimatedDuration) ? (
                      <>
                        {chat.job.application?.coverLetter && (
                          <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                              Cover Letter
                            </p>
                            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap break-words">
                              {chat.job.application.coverLetter}
                            </p>
                          </div>
                        )}
                        
                        {chat.job.application?.estimatedDuration && (
                          <div className={`pt-2 ${chat.job.application?.coverLetter ? '' : 'border-t border-gray-200 dark:border-gray-600'}`}>
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                Estimated Duration
                              </p>
                              <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                                {chat.job.application.estimatedDuration} {chat.job.duration?.unit ? chat.job.duration.unit.charAt(0).toUpperCase() + chat.job.duration.unit.slice(1) : 'Days'}
                              </p>
                            </div>
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
              )}

              {chat.gig && (() => {
                // Find the accepted order for this chat to get package type
                const acceptedOrder = chat.gig.orders?.find(order => 
                  order.status === 'accepted' && 
                  (order.client?.toString() === clientParticipant?.user?._id?.toString() || 
                   order.client?.toString() === clientParticipant?.user?.toString())
                );
                const packageType = chat.gig.order?.package || acceptedOrder?.package || 'basic';
                const packagePrice = packageType === 'premium' 
                  ? chat.gig.pricing?.premium?.price 
                  : chat.gig.pricing?.basic?.price;
                
                return (
                  <div>
                    <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                      Gig Details
                    </h4>
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {chat.gig.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                            {chat.gig.type}
                          </span>
                          <span className="text-xs text-gray-400 dark:text-gray-500">•</span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                            packageType === 'premium' 
                              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' 
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          }`}>
                            {packageType === 'premium' ? 'Premium' : 'Basic'}
                          </span>
                          {packagePrice && (
                            <>
                              <span className="text-xs text-gray-400 dark:text-gray-500">•</span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                ${packagePrice}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {chat.gig.order?.requirements && (
                        <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                            Requirements
                          </p>
                          <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap break-words">
                            {chat.gig.order.requirements}
                          </p>
                        </div>
                      )}
                      
                      {chat.gig.order?.timeline && (
                        <div className={`pt-2 ${chat.gig.order?.requirements ? '' : 'border-t border-gray-200 dark:border-gray-600'}`}>
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                              Timeline
                            </p>
                            <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                              {chat.gig.order.timeline} days
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div>
                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Chat Status
                </h4>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(chat.status)}`}>
                    {getStatusIcon(chat.status)}
                    <span className="ml-1 capitalize">{chat.status}</span>
                  </span>
                </div>
              </div>

              {/* Quick Access Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Quick Access
                  </h4>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => setQuickAccessFilter('all')}
                      className={`px-2 py-1 text-xs rounded ${
                        quickAccessFilter === 'all' 
                          ? 'bg-accent text-white' 
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setQuickAccessFilter('links')}
                      className={`px-2 py-1 text-xs rounded ${
                        quickAccessFilter === 'links' 
                          ? 'bg-accent text-white' 
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Links
                    </button>
                    <button
                      onClick={() => setQuickAccessFilter('images')}
                      className={`px-2 py-1 text-xs rounded ${
                        quickAccessFilter === 'images' 
                          ? 'bg-accent text-white' 
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Images
                    </button>
                    <button
                      onClick={() => setQuickAccessFilter('files')}
                      className={`px-2 py-1 text-xs rounded ${
                        quickAccessFilter === 'files' 
                          ? 'bg-accent text-white' 
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Files
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {getQuickAccessItems().length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      No {quickAccessFilter === 'all' ? 'items' : quickAccessFilter} found
                    </p>
                  ) : (
                    getQuickAccessItems().map((item, index) => (
                      <div key={`${item.messageId}-${index}`} className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        {item.type === 'link' || quickAccessFilter === 'links' ? (
                          <div className="flex items-center space-x-2">
                            <Link className="w-4 h-4 text-blue-500" />
                            <div className="flex-1 min-w-0">
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate block"
                              >
                                {item.url.length > 30 ? `${item.url.substring(0, 30)}...` : item.url}
                              </a>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {item.sender} • {formatTime(item.timestamp)}
                              </p>
                            </div>
                            <button
                              onClick={() => window.open(item.url, '_blank')}
                              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          </div>
                        ) : item.type === 'image' || quickAccessFilter === 'images' ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <Image className="w-4 h-4 text-green-500" />
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {item.sender} • {formatTime(item.timestamp)}
                                </span>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const attachmentIndex = item.attachmentIndex !== undefined ? item.attachmentIndex : 
                                    (messages.find(m => m._id === item.messageId)?.attachments?.findIndex(att => att.url === item.url) ?? -1);
                                  handleDownload(item.url, item.filename || item.attachment?.originalName || 'image', item.messageId, attachmentIndex);
                                }}
                                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                title="Download"
                              >
                                <Download className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="relative">
                              {item.url ? (
                                <img
                                  src={item.url}
                                  alt={item.filename || item.attachment?.originalName || 'Shared image'}
                                  className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-90"
                                  onClick={() => window.open(item.url, '_blank')}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'block';
                                  }}
                                />
                              ) : null}
                              <div className="w-full h-24 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center" style={{ display: item.url ? 'none' : 'flex' }}>
                                <Image className="w-8 h-8 text-gray-400" />
                              </div>
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
                                {item.filename || item.attachment?.originalName || 'Image'}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <File className="w-4 h-4 text-orange-500" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                                {item.filename || item.attachment?.originalName || 'File'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {item.sender} • {formatTime(item.timestamp)}
                              </p>
                              {(item.attachment?.size || item.size) && (
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                  {formatFileSize(item.attachment?.size || item.size)}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                const attachmentIndex = item.attachmentIndex !== undefined ? item.attachmentIndex : 
                                  (messages.find(m => m._id === item.messageId)?.attachments?.findIndex(att => att.url === item.url) ?? -1);
                                handleDownload(item.url, item.filename || item.attachment?.originalName || 'file', item.messageId, attachmentIndex);
                              }}
                              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Disbursement Modal */}
      {showDisburseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Disburse Funds
            </h3>
            
            <div className="space-y-4">
              {jobDetails && (
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Total Amount:</span>
                      <div className="text-right">
                        <span className="font-semibold">{jobDetails.totalAmountETH.toFixed(6)} USDC</span>
                        {ethPrice && (
                          <span className="text-gray-500 dark:text-gray-400 ml-2">
                            (${(jobDetails.totalAmountETH * ethPrice).toFixed(2)})
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Remaining:</span>
                      <div className="text-right">
                        <span className="font-semibold text-green-600 dark:text-green-400">
                          {jobDetails.remainingAmountETH.toFixed(6)} USDC
                        </span>
                        {ethPrice && (
                          <span className="text-gray-500 dark:text-gray-400 ml-2">
                            (${(jobDetails.remainingAmountETH * ethPrice).toFixed(2)})
                          </span>
                        )}
                      </div>
                    </div>
                    {ethPrice && jobDetails.remainingAmountETH && (
                      <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
                        <span className="text-gray-600 dark:text-gray-400">Max Disbursement (80%):</span>
                        <span className="font-semibold text-blue-600 dark:text-blue-400">
                          ${Math.floor(jobDetails.remainingAmountETH * ethPrice * 0.8)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Disbursement Amount (USD)
                </label>
                <input
                  type="number"
                  min="1"
                    max={jobDetails && ethPrice ? Math.floor(jobDetails.remainingAmountETH * ethPrice * 0.8) : undefined}
                  value={disburseAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow positive integers
                    if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                      // Check max limit (80% of remaining)
                      if (jobDetails && ethPrice) {
                        const maxAmount = Math.floor(jobDetails.remainingAmountETH * ethPrice * 0.8);
                        if (value === '' || Number(value) <= maxAmount) {
                          setDisburseAmount(value);
                        }
                      } else {
                        setDisburseAmount(value);
                      }
                    }
                  }}
                  placeholder="Enter amount to disburse"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent dark:bg-gray-700 dark:text-gray-100"
                />
                {disburseAmount && !isNaN(disburseAmount) && Number(disburseAmount) > 0 && ethPrice && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    ≈ {(Number(disburseAmount) / ethPrice).toFixed(6)} USDC
                  </p>
                )}
                {jobDetails && ethPrice && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Maximum: ${Math.floor(jobDetails.remainingAmountETH * ethPrice * 0.8)} (80% of remaining)
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowDisburseModal(false);
                  setDisburseAmount('');
                }}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEscrowDisburse}
                disabled={escrowLoading.disburse || !disburseAmount || isNaN(disburseAmount) || Number(disburseAmount) < 1 || (jobDetails && ethPrice && Number(disburseAmount) > Math.floor(jobDetails.remainingAmountETH * ethPrice * 0.8))}
                className="flex-1 px-4 py-2 bg-accent text-white rounded-md hover:bg-accent-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {escrowLoading.disburse ? 'Processing...' : 'Confirm Disburse'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Price Reset Modal */}
      {showPriceResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Reset Project Price
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  New Price ($)
                </label>
                <input
                  type="number"
                  min="1"
                  value={resetPrice}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow positive integers
                    if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                      setResetPrice(value);
                    }
                  }}
                  placeholder="Enter new price"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason (Optional)
                </label>
                <textarea
                  value={resetReason}
                  onChange={(e) => setResetReason(e.target.value)}
                  placeholder="Why are you changing the price?"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowPriceResetModal(false);
                  setResetPrice('');
                  setResetReason('');
                }}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePriceReset}
                disabled={updatePriceMutation.isLoading}
                className="flex-1 px-4 py-2 bg-accent text-white rounded-md hover:bg-accent-dark transition-colors disabled:opacity-50"
              >
                {updatePriceMutation.isLoading ? 'Updating...' : 'Update Price'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Participant Confirmation Modal */}
      {showRemoveParticipantModal && participantToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Remove Participant
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to remove <span className="font-medium">{participantToRemove.participant?.user?.username || 'this participant'}</span> from this chat? They will no longer be able to view or send messages.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRemoveParticipantModal(false);
                  setParticipantToRemove(null);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveParticipant}
                disabled={removeParticipantMutation.isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {removeParticipantMutation.isLoading ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatDetailPage;